import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import axios from "axios";
import { Spinner } from "reactstrap";
import { Box, Button, Typography } from "@mui/material";

import configuration from "../../configuration.json";
import {
  identityConfig,
  getInternalUserId,
} from "../../clients/identityClient";
import { getUserProfile } from "../../clients/authClient";
import { clerkLogout } from "../../clients/clerkBridge";
import utilFunctions from "../../utilities/utilFunctions";

const TEMPLATE = "spec-api";
const CLAIM = "https://spec.app/userId";
const RESOLVE_ATTEMPTS = 3;
const RESOLVE_BACKOFF_MS = 400;

// This flow fails across three different systems (Clerk, our auth API, the
// browser session) and the failures look identical from the UI. Log enough to
// tell them apart. Note the redirects wipe the console -- turn on "Preserve log"
// before reproducing, or re-wire utilities/authTrace.js for a trail that
// survives navigation.
const logFailure = (step, err) => {
  const res = err?.response;
  console.log(
    `[auth/complete] failed at: ${step}`,
    res
      ? { status: res.status, url: err?.config?.url, data: res.data }
      : err
  );
};

/**
 * The login-completion gate.
 *
 * Clerk has no server-side hook at token-mint time, so on a first-ever login the
 * token is minted with no `https://spec.app/userId` claim. This route is the only
 * thing in the system allowed to create a Spec user: it calls the backend to
 * link-or-create, then re-mints the token so it carries our GUID.
 *
 * Returning users already have the claim and fall straight through.
 *
 * This must be a PUBLIC route -- the user is not `store.authenticated` yet, and
 * ProtectedRoute would bounce them back to /login in a loop.
 */
const AuthComplete = ({ handleLogInSuccess }) => {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  // Guards against a double-invoke (StrictMode, or a re-render before the async
  // work settles). POST /users/me/resolve is idempotent, but there is no reason
  // to fire it twice.
  const started = useRef(false);

  useEffect(() => {
    if (!isLoaded || started.current) {
      return;
    }
    if (!isSignedIn) {
      navigate("/login");
      return;
    }
    started.current = true;

    const completeLogin = async () => {
      let step = "get-token";
      try {
        // The default Clerk session token is the wrong shape (no `aud`, 60s
        // lifetime). The `spec-api` template is what the APIs actually accept,
        // and its 12h lifetime is what lets the synchronous token seam and
        // session.js's exp poll stay exactly as they are.
        let token = await getToken({ template: TEMPLATE });
        let claims = token ? utilFunctions.parseJwt(token) : null;

        // A null token here means Clerk has a user but would not mint against
        // the template -- almost always a missing/misnamed `spec-api` template.
        // Say so, rather than sending "Bearer null" to the API and reporting
        // whatever it makes of that.
        if (!token) {
          console.log(
            `[auth/complete] Clerk returned no token for template "${TEMPLATE}". ` +
              `Check that the JWT template exists and is named exactly that.`
          );
          setError("We couldn't finish signing you in.");
          return;
        }

        if (!claims?.[CLAIM]) {
          step = "resolve";
          // First-ever login. Ask the API to link-or-create us. Send no body:
          // the API reads `sub` from the verified token and pulls the linked
          // social accounts from Clerk server-side. If the browser could supply
          // a provider subject, anyone could link their new Clerk account onto
          // an existing Spec user and inherit their establishments.
          await axios.post(
            `${configuration.authBaseUrl}/users/me/resolve`,
            null,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          // Re-mint until the claim shows up. `skipCache` is load-bearing --
          // without it Clerk hands back the cached pre-resolve token and the
          // claim never appears.
          step = "re-mint";
          for (let i = 0; i < RESOLVE_ATTEMPTS && !claims?.[CLAIM]; i++) {
            if (i > 0) {
              await new Promise((r) => setTimeout(r, RESOLVE_BACKOFF_MS));
            }
            token = await getToken({ template: TEMPLATE, skipCache: true });
            claims = token ? utilFunctions.parseJwt(token) : null;
          }
        }

        // Never fall through without the claim. Every GetUserId() downstream
        // would resolve to the Clerk user id instead of our GUID, silently
        // writing rows against a user that does not exist. Failing loudly here
        // is far cheaper.
        if (!claims?.[CLAIM]) {
          console.log(
            `[auth/complete] resolve succeeded but "${CLAIM}" never appeared ` +
              `after ${RESOLVE_ATTEMPTS} re-mints. Check that the spec-api ` +
              `template maps the claim to public_metadata.`
          );
          setError("We couldn't finish setting up your account.");
          return;
        }

        identityConfig.accessToken = token;
        localStorage.setItem("at", token);

        step = "profile-fetch";
        const user = await getUserProfile();
        // Skip the split.io reload only when there is no profile yet, so the
        // user is routed into profile creation instead of the establishments page.
        const skipReload = user?.profile === null;
        handleLogInSuccess(user.profile, getInternalUserId(claims), skipReload);

        // handleLogInSuccess hard-redirects to /establishments unless skipReload.
        if (skipReload) {
          navigate("/create-profile");
        }
      } catch (err) {
        logFailure(step, err);
        // A 400 from the resolve step specifically means the API authenticated
        // the request but the token's subject was not a Clerk `user_...` id --
        // i.e. an IdentityServer4 token reached it. Point at that directly
        // rather than making someone read the backend to work it out.
        if (step === "resolve" && err?.response?.status === 400) {
          console.log(
            "[auth/complete] a non-Clerk token reached users/me/resolve. " +
              "Clear the stale 'at' value from localStorage and sign in again."
          );
        }
        setError("We couldn't finish signing you in.");
      }
    };

    completeLogin();
  }, [isLoaded, isSignedIn]);

  const handleSignOut = () => {
    // Falls back to /login if the bridge has not registered yet -- otherwise a
    // failed gate could strand the user on a dead screen.
    if (!clerkLogout()) {
      navigate("/login");
    }
  };

  return (
    <Box
      sx={{
        background: "#000000",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 6,
      }}
    >
      {error ? (
        <>
          <Typography
            variant="h3"
            sx={{ color: "#fff", textAlign: "center", marginBottom: 4 }}
          >
            {error}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#fff",
              textAlign: "center",
              marginBottom: 6,
              maxWidth: "450px",
            }}
          >
            Please try again. If this keeps happening, contact support.
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{ textTransform: "none" }}
            >
              Retry
            </Button>
            <Button
              variant="outlined"
              onClick={handleSignOut}
              sx={{ color: "#fff", borderColor: "#fff", textTransform: "none" }}
            >
              Sign out
            </Button>
          </Box>
        </>
      ) : (
        <Spinner color="primary" size="lg" />
      )}
    </Box>
  );
};

export default AuthComplete;
